import React from "react";
import {
  WmProgressIndicator,
  WmProgressMonitor,
  WmProgressSlice
} from "@watermarkinsights/ripple-react";
import "../../../common/Insights.scss";
import translate from "../../../../i18n/translate";
import { useIntl } from "react-intl";
import { MissionAndOutcomesHtmlState } from "./MissionAndOutcomesExportView";
import { popover } from "./CoursesChart";
import ApiError from "@/features/common/elements/ApiError";
import { OutcomesTerminology } from "@/contexts/AppContext";
import { useFormatMessage } from "@/features/common/helper";

interface ProgramChartProps {
  programData: MissionAndOutcomesHtmlState;
  outcomesTerminology?: OutcomesTerminology | null;
}

export default function ProgramChart({ programData, outcomesTerminology }: ProgramChartProps) {
  return (
    <React.Fragment>
      <div>
        <h3 className="title organization-progress">
          {translate("app.programProgressCard.programProgressDefinition")}
        </h3>
        {programData.programApiError ? (
          <ApiError containerClasses="api-error" />
        ) : (
          <>
            <p className="org totals">
              {translate("app.programProgressCard.totalPrograms", {
                totalPrograms: programData.totalPrograms
              })}
            </p>
            <div id="org-charts-html">
              <ProgramProgressChart programData={programData} outcomesTerminology={outcomesTerminology} />
            </div>
          </>
        )}
      </div>
    </React.Fragment>
  );
}
interface ProgramProgressChartProps {
  programData: MissionAndOutcomesHtmlState;
  outcomesTerminology?: OutcomesTerminology | null;
}
const ProgramProgressChart = ({ programData, outcomesTerminology }: ProgramProgressChartProps) => {
  const formatMessage = useFormatMessage();
  const programsWithMission = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithMission"
  });

  const added = useIntl().formatMessage({
    id: "app.drilldown.added"
  });
  const notAdded = useIntl().formatMessage({
    id: "app.drilldown.notAdded"
  });
  const created = useIntl().formatMessage({
    id: "app.drilldown.created"
  });

  const notCreated = useIntl().formatMessage({
    id: "app.drilldown.notCreated"
  });

  const mapped = useIntl().formatMessage({
    id: "app.drilldown.mapped"
  });

  const notMapped = useIntl().formatMessage({
    id: "app.drilldown.notMapped"
  });
  const missionStatement = useIntl().formatMessage({
    id: "app.orgProgressCard.missionStatement"
  });
  const programsWithoutMission = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithoutMission"
  });
  const learningOutcomes = formatMessage(
    { id: "app.orgProgressCard.learningOutcomes" },
    { learning_outcome_plural: outcomesTerminology?.learningOutcomes?.plural || "..." }
  );
  const successOutcomes = formatMessage(
    { id: "app.orgProgressCard.successOutcomes" },
    { success_outcome_plural: outcomesTerminology?.successOutcomes?.plural || "..." }
  );
  const ILOMapped = useIntl().formatMessage({
    id: "app.orgProgressCard.ILOMapped"
  });
  const curriculumMap = useIntl().formatMessage({
    id: "app.orgProgressCard.curriculumMap"
  });

  const programsWithLO = formatMessage(
    { id: "app.programProgressCard.programsWithLO" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural?.toLowerCase() || "..." }
  );
  const programsWithoutLO = formatMessage(
    { id: "app.programProgressCard.programsWithoutLO" },
    { learning_outcome_plural_lowercase: outcomesTerminology?.learningOutcomes?.plural?.toLowerCase() || "..." }
  );
  const programsWithSO = formatMessage(
    { id: "app.programProgressCard.programsWithSO" },
    { success_outcome_plural_lowercase: outcomesTerminology?.successOutcomes?.plural?.toLowerCase() || "..." }
  );
  const programsWithoutSO = formatMessage(
    { id: "app.programProgressCard.programsWithoutSO" },
    { success_outcome_plural_lowercase: outcomesTerminology?.successOutcomes?.plural?.toLowerCase() || "..." }
  );
  const programsWithCM = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithCM"
  });
  const programsWithoutCM = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithoutCM"
  });
  const programsWithILO = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithILO"
  });
  const programsWithoutILO = useIntl().formatMessage({
    id: "app.programProgressCard.programsWithoutILO"
  });

  return (
    <div id="prog-charts">
      <WmProgressMonitor
        id="program-progress"
        print-mode={true}
        bar-labels-width="5rem"
      >
        <WmProgressIndicator label={missionStatement}>
          <WmProgressSlice
            legend={added}
            amount={programData.programMissionComplete.toString()}
            popoverTitle={popover(
              programData.programMissionComplete,
              programData.totalPrograms
            )}
            popoverText={programsWithMission}
          />
          <WmProgressSlice
            legend={notAdded}
            amount={programData.programMissionIncomplete.toString()}
            popoverTitle={popover(
              programData.programMissionIncomplete,
              programData.totalPrograms
            )}
            popoverText={programsWithoutMission}
          />
        </WmProgressIndicator>
        <WmProgressIndicator label={learningOutcomes}>
          <WmProgressSlice
            legend={created}
            amount={programData.programLearningOutcomes.toString()}
            popoverTitle={popover(
              programData.programLearningOutcomes,
              programData.totalPrograms
            )}
            popoverText={programsWithLO}
          />
          <WmProgressSlice
            legend={notCreated}
            popoverTitle={popover(
              programData.programLearningOutcomesIncomplete,
              programData.totalPrograms
            )}
            popoverText={programsWithoutLO}
            amount={programData.programLearningOutcomesIncomplete.toString()}
          />
        </WmProgressIndicator>
        <WmProgressIndicator label={successOutcomes}>
          <WmProgressSlice
            legend={created}
            amount={programData.programSuccessOutcomes.toString()}
            popoverTitle={popover(
              programData.programSuccessOutcomes,
              programData.totalPrograms
            )}
            popoverText={programsWithSO}
          />
          <WmProgressSlice
            legend={notCreated}
            amount={programData.programSuccessOutcomesIncomplete.toString()}
            popoverTitle={popover(
              programData.programSuccessOutcomesIncomplete,
              programData.totalPrograms
            )}
            popoverText={programsWithoutSO}
          />
        </WmProgressIndicator>
        <WmProgressIndicator label={curriculumMap}>
          <WmProgressSlice
            legend={mapped}
            amount={programData.programCurriculumMapsComplete.toString()}
            popoverTitle={popover(
              programData.programCurriculumMapsComplete,
              programData.totalPrograms
            )}
            popoverText={programsWithCM}
          />
          <WmProgressSlice
            legend={notMapped}
            amount={programData.programCurriculumMapsIncomplete.toString()}
            popoverTitle={popover(
              programData.programCurriculumMapsIncomplete,
              programData.totalPrograms
            )}
            popoverText={programsWithoutCM}
          />
        </WmProgressIndicator>
        <WmProgressIndicator label={ILOMapped}>
          <WmProgressSlice
            legend={mapped}
            amount={programData.iloProgramMappingsComplete.toString()}
            popoverTitle={popover(
              programData.iloProgramMappingsComplete,
              programData.totalPrograms
            )}
            popoverText={programsWithILO}
          />
          <WmProgressSlice
            legend={notMapped}
            amount={programData.iloProgramMappingsIncomplete.toString()}
            popoverTitle={popover(
              programData.iloProgramMappingsIncomplete,
              programData.totalPrograms
            )}
            popoverText={programsWithoutILO}
          />
        </WmProgressIndicator>
      </WmProgressMonitor>
    </div>
  );
};
